/**
 * A very small IndexedDB wrapper — one object store, get/put/delete.
 *
 * This is a *cache*, never the source of truth: the server holds authoritative progress (locked
 * decision, Ch1). If IndexedDB is unavailable (private mode, blocked storage) every call
 * degrades to a no-op and the game keeps working online.
 */
const DB_NAME = 'nightband';
const DB_VERSION = 1;
const STORE = 'kv';

let dbPromise: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      return resolve(null);
    }
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    let request: IDBRequest<T>;
    try {
      request = fn(db.transaction(STORE, mode).objectStore(STORE));
    } catch {
      return resolve(null);
    }
    request.onsuccess = () => resolve(request.result ?? null);
    request.onerror = () => resolve(null);
  });
}

export const idb = {
  get: <T>(key: string): Promise<T | null> =>
    withStore<T>('readonly', (store) => store.get(key) as IDBRequest<T>),

  set: (key: string, value: unknown): Promise<unknown> =>
    withStore('readwrite', (store) => store.put(value, key) as IDBRequest<unknown>),

  delete: (key: string): Promise<unknown> =>
    withStore('readwrite', (store) => store.delete(key) as IDBRequest<unknown>),
};
